import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import LandingPage from './pages/LandingPage';
import SignUpPage from './pages/SignUpPage';
import LoginPage from './pages/LoginPage';
import PaymentPage from './pages/PaymentPage';
import Dashboard from './pages/Dashboard';
import ProjectDetail from './pages/ProjectDetail';
import AdminDashboard from './pages/AdminDashboard';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PromoPage from './pages/PromoPage';
import DataCollectionPage from './pages/DataCollectionPage';
import SurveyBuilderPage from './pages/SurveyBuilderPage';
import SurveyRespondPage from './pages/SurveyRespondPage';
import SurveyResponsesPage from './pages/SurveyResponsesPage';
import InterviewBuilderPage from './pages/InterviewBuilderPage';
import InterviewRecordPage from './pages/InterviewRecordPage';
import InterviewResponsesPage from './pages/InterviewResponsesPage';
import SampleSizeCalculator from './pages/SampleSizeCalculator';
import LiteratureReview from './pages/LiteratureReview';
import ResearchMethodology from './pages/ResearchMethodology';
import ResearchProposal from './pages/ResearchProposal';
import CollaboratorLogin from './pages/CollaboratorLogin';
import CollaboratorDashboard from './pages/CollaboratorDashboard';
import SupervisorAnalysisReview from './pages/SupervisorAnalysisReview';
import './App.css';

const ProtectedRoute = ({ children, requireSubscription = false, allowCollaborator = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  
  // Collaborators: only allowed on specific routes; else bounce to their dashboard
  if (user.role === 'collaborator') {
    if (!allowCollaborator) {
      return <Navigate to="/collaborator-dashboard" replace />;
    }
    return children;
  }
  
  if (requireSubscription && user.subscription_status !== 'active') {
    return <Navigate to="/payment" replace />;
  }
  
  return children;
};

const CollaboratorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-700"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/collaborator-login" replace />;
  if (user.role !== 'collaborator') return <Navigate to="/dashboard" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  
  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
  }
  
  if (user && user.subscription_status === 'active') {
    // Redirect admins to admin dashboard, others to user dashboard
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'collaborator') {
      return <Navigate to="/collaborator-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/promo" element={<PromoPage />} />
      <Route path="/signup" element={<PublicRoute><SignUpPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/collaborator-login" element={<PublicRoute><CollaboratorLogin /></PublicRoute>} />
      <Route path="/collaborator-dashboard" element={<CollaboratorRoute><CollaboratorDashboard /></CollaboratorRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute requireSubscription><Dashboard /></ProtectedRoute>} />
      <Route path="/project/:projectId" element={<ProtectedRoute requireSubscription allowCollaborator><ProjectDetail /></ProtectedRoute>} />
      
      {/* Data Collection Routes */}
      <Route path="/project/:projectId/data-collection" element={<ProtectedRoute requireSubscription><DataCollectionPage /></ProtectedRoute>} />
      <Route path="/project/:projectId/survey/new" element={<ProtectedRoute requireSubscription><SurveyBuilderPage /></ProtectedRoute>} />
      <Route path="/project/:projectId/survey/:surveyId/edit" element={<ProtectedRoute requireSubscription><SurveyBuilderPage /></ProtectedRoute>} />
      <Route path="/project/:projectId/survey/:surveyId/responses" element={<ProtectedRoute requireSubscription><SurveyResponsesPage /></ProtectedRoute>} />
      <Route path="/project/:projectId/interview/new" element={<ProtectedRoute requireSubscription><InterviewBuilderPage /></ProtectedRoute>} />
      <Route path="/project/:projectId/interview/:interviewId/edit" element={<ProtectedRoute requireSubscription><InterviewBuilderPage /></ProtectedRoute>} />
      <Route path="/project/:projectId/interview/:interviewId/record" element={<ProtectedRoute requireSubscription><InterviewRecordPage /></ProtectedRoute>} />
      <Route path="/project/:projectId/interview/:interviewId/responses" element={<ProtectedRoute requireSubscription><InterviewResponsesPage /></ProtectedRoute>} />
      
      {/* Research Tools Routes */}
      <Route path="/project/:projectId/sample-size" element={<ProtectedRoute requireSubscription><SampleSizeCalculator /></ProtectedRoute>} />
      <Route path="/project/:projectId/literature-review" element={<ProtectedRoute requireSubscription allowCollaborator><LiteratureReview /></ProtectedRoute>} />
      <Route path="/project/:projectId/methodology" element={<ProtectedRoute requireSubscription allowCollaborator><ResearchMethodology /></ProtectedRoute>} />
      <Route path="/project/:projectId/proposal" element={<ProtectedRoute requireSubscription allowCollaborator><ResearchProposal /></ProtectedRoute>} />
      <Route path="/project/:projectId/analysis-review" element={<ProtectedRoute requireSubscription allowCollaborator><SupervisorAnalysisReview /></ProtectedRoute>} />
      
      {/* Public Survey Response Route */}
      <Route path="/survey/:shareCode" element={<SurveyRespondPage />} />
      
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
